<script setup lang="ts">
import { nextTick, onMounted, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'

export interface TabStripItem {
  key: string
  label: string
  count?: number
}

const props = defineProps<{
  modelValue: string
  tabs: TabStripItem[]
}>()

const emit = defineEmits<{
  'update:modelValue': [key: string]
}>()

function select(key: string): void {
  emit('update:modelValue', key)
}

// On narrow screens the tab row scrolls horizontally on its own (see the
// wrapping overflow-x-auto container in the template) instead of pushing the
// whole page into a sideways scroll. Keep the active tab visible whenever it
// changes, including on initial mount, in case it starts off-screen.
const tabRefs = new Map<string, HTMLButtonElement>()

function setTabRef(key: string, el: Element | ComponentPublicInstance | null): void {
  if (el instanceof HTMLButtonElement) {
    tabRefs.set(key, el)
  } else {
    tabRefs.delete(key)
  }
}

function scrollActiveTabIntoView(): void {
  const el = tabRefs.get(props.modelValue)
  // jsdom (used by Vitest) does not implement scrollIntoView, guard defensively.
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }
}

onMounted(scrollActiveTabIntoView)
watch(() => props.modelValue, () => void nextTick(scrollActiveTabIntoView))
</script>

<template>
  <div class="overflow-x-auto">
    <div class="flex items-center gap-1 border-b border-slate-700">
      <button
        v-for="tab in props.tabs"
        :key="tab.key"
        :ref="(el) => setTabRef(tab.key, el)"
        type="button"
        class="px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px focus:outline-none whitespace-nowrap shrink-0"
        :class="modelValue === tab.key
          ? 'border-emerald-400 text-white font-medium'
          : 'border-transparent text-slate-400 hover:text-white hover:border-slate-500'"
        @click="select(tab.key)"
      >
        {{ tab.label }}
        <span
          v-if="tab.count !== undefined"
          class="ml-1.5 inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-medium"
          :class="modelValue === tab.key
            ? 'bg-emerald-400/20 text-emerald-300'
            : 'bg-slate-700 text-slate-400'"
        >
          {{ tab.count }}
        </span>
      </button>
    </div>
  </div>
</template>
